import {
    MonthView,
    DayView,
    WorkWeekView,
    Scheduler,
    SchedulerDataChangeEvent,
    SchedulerItem,
    SchedulerSlot,
    SchedulerSlotProps,
    SchedulerItemProps,
} from "@progress/kendo-react-scheduler"
import {classNames} from "@progress/kendo-react-common"
import dayjs from "dayjs"
import {useState, useEffect} from "react"
import {SchedulesAndBreak, setSelectedSlot, setShowAddEditPopUp} from "../../containers/DVSASchedules/schedulesSlice"
import {RootState} from "../../rootReducer"
import {useAppDispatch, useAppSelector} from "../../store"
import {hasPermission} from "../Shared/permissionChecker"
import {LoadingPanel} from "../../shared/CommonItems"

const DVSAScheduler = () => {
    const dispatch = useAppDispatch()
    const loading = useAppSelector((state: RootState) => state.containers.schedules.loading)
    const schedules = useAppSelector((state: RootState) => state.containers.schedules.schedules)
    const [translatedSchedules, setTranslatedSchedules] = useState([])
    const assignedPermissions = useAppSelector((state: RootState) => state.application.permissions)
    const [selectedDate, setSelectedDate] = useState(null)
    const [activeView, setActiveView] = useState("month")

    useEffect(() => {
        if (schedules) {
            const newSchs: any = []
            schedules.forEach((schedule: SchedulesAndBreak) => {
                const {
                    dvsaAvailabilityID,
                    eventTitle,
                    startDateTime,
                    endDateTime,
                    recurringGroup,
                    conflicts,
                    recType,
                    minTestStartDate,
                    maxTestEndDate,
                } = schedule
                newSchs.push({
                    id: dvsaAvailabilityID,
                    title: eventTitle,
                    start: new Date(startDateTime),
                    end: new Date(endDateTime),
                    recurrenceRule: null,
                    recurrenceId: recurringGroup,
                    recurrenceExceptions: null,
                    isAllDay: false,
                    description: recurringGroup,
                    conflicts,
                    recType,
                    minTestStartDate,
                    maxTestEndDate,
                })
            })

            setTranslatedSchedules(newSchs)
        }
    }, [schedules])

    const onDateChange = (props: any) => {
        setSelectedDate(props.value)
    }
    const onViewChange = (props: any) => {
        setActiveView(props.target.value)
    }

    const handleDataChange = ({created, updated, deleted}: SchedulerDataChangeEvent) => {}

    const verifyPermissions = (permissions: string[]) => hasPermission(permissions, assignedPermissions)

    const CustomItem = (props: SchedulerItemProps) => {
        const isEditable =
            dayjs(props.start).toDate() >= dayjs().startOf("day").toDate() && verifyPermissions(["DVSASchedules.Add"])
        const dataItem = props.dataItem as SchedulesAndBreak
        const className = classNames({
            "k-disabled": !isEditable,
            "break-color": dataItem.recType === "BR",
            "available-color": dataItem.recType === "SCH",
        })

        return (
            <SchedulerItem
                {...props}
                className={className}
                onDoubleClick={() => {
                    // eslint-disable-next-line no-lone-blocks

                    dispatch(
                        setSelectedSlot({
                            start: dayjs(props.dataItem.start).toDate(),
                            end: dayjs(props.dataItem.end).toDate(),
                            breakStart: null,
                            breakEnd: null,
                            id: props.dataItem.id,
                            recurrendeId: props.dataItem.recurrenceId,
                            conflicts: props.dataItem.conflicts,
                            recType: props.dataItem.recType,
                            minTestStartDate:
                                props.dataItem.minTestStartDate !== null
                                    ? dayjs(props.dataItem.minTestStartDate).toDate()
                                    : null,
                            maxTestEndDate:
                                props.dataItem.maxTestEndDate !== null
                                    ? dayjs(props.dataItem.maxTestEndDate).toDate()
                                    : null,
                        }),
                    )
                    dispatch(setShowAddEditPopUp(true))
                }}
            />
        )
    }

    const CustomSlot = (props: SchedulerSlotProps) => {
        const scheduledAlready = schedules.find(
            x => dayjs(x.startDateTime).format("YYYYMMDD") === dayjs(props.start).format("YYYYMMDD"),
        )
        const slotDate = dayjs(props.start).toDate()
        const todaysDate = dayjs().startOf("day").toDate()

        const isEditable =
            slotDate >= todaysDate && scheduledAlready === undefined && verifyPermissions(["DVSASchedules.Add"])

        return (
            <SchedulerSlot
                {...props}
                className={!isEditable ? "k-disabled" : ""}
                onClick={() => {
                    if (isEditable) {
                        dispatch(
                            setSelectedSlot({
                                start: dayjs(props.start).toDate(),
                                end: dayjs(props.start).toDate(),
                                breakStart: dayjs(props.start).toDate(),
                                breakEnd: dayjs(props.start).toDate(),
                                id: null,
                                recurrendeId: null,
                                recType: "",
                                conflicts: 0,
                                minTestStartDate: null,
                                maxTestEndDate: null,
                            }),
                        )
                        dispatch(setShowAddEditPopUp(true))
                    }
                }}
                onDoubleClick={() => {}}
            />
        )
    }

    return (
        <>
            {loading && <LoadingPanel />}
            <Scheduler
                id="dvsaSchedulerView"
                date={selectedDate ?? new Date()}
                view={activeView}
                data={translatedSchedules}
                editable={{add: true, edit: true, remove: true, drag: false, resize: false, select: false}}
                item={CustomItem}
                slot={CustomSlot}
                onDataChange={handleDataChange}
                onDateChange={onDateChange}
                onViewChange={onViewChange}
                height="calc(100vh - 13.2rem)"
            >
                <MonthView />
                <DayView />
                <WorkWeekView showWorkHours={true} />
            </Scheduler>
        </>
    )
}

export default DVSAScheduler
